import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Clock, AlertCircle, DollarSign, Calendar, Building2, CreditCard, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList } from 'recharts';

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [resumoAlertas, setResumoAlertas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [filtros, setFiltros] = useState({
    data_inicio: '',
    data_fim: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim);

      const [dashResponse, alertasResponse, resumoResponse] = await Promise.all([
        api.get(`/dashboard?${params.toString()}`),
        api.get('/alertas'),
        api.get('/alertas/resumo')
      ]);
      setData(dashResponse.data);
      setAlertas(alertasResponse.data);
      setResumoAlertas(resumoResponse.data);
    } catch (error) {
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const chartDataPagar = [
    { name: 'Em Aberto', value: data?.pagar?.em_aberto || 0 },
    { name: 'Vencido', value: data?.pagar?.vencido || 0 },
    { name: 'Pago', value: data?.pagar?.pago || 0 },
  ];

  const chartDataReceber = [
    { name: 'Em Aberto', value: data?.receber?.em_aberto || 0 },
    { name: 'Vencido', value: data?.receber?.vencido || 0 },
    { name: 'Recebido', value: data?.receber?.recebido || 0 },
  ];

  const chartDataBancos = Object.entries(data?.por_banco || {}).map(([banco, valores]) => ({
    banco: banco === 'Não informado' ? 'Sem Banco' : banco,
    pagar: valores.pagar,
    receber: valores.receber
  }));

  const bancosIndividuais = ['BB BoraMais', 'BB BoraCar', 'SICOOB', 'Caixa Local'];
  const dadosPorBanco = bancosIndividuais.map(banco => {
    const valores = data?.por_banco?.[banco] || { pagar: 0, receber: 0 };
    const saldo = valores.receber - valores.pagar;
    return {
      banco,
      entradas: valores.receber,
      saidas: valores.pagar,
      saldo,
      dados: [
        { name: 'Entradas', value: valores.receber },
        { name: 'Saídas', value: valores.pagar }
      ]
    };
  });

  const chartDataFormasPagamento = Object.entries(data?.por_forma_pagamento || {}).map(([forma, valor]) => ({
    name: forma,
    value: valor
  }));

  const COLORS = ['#3b82f6', '#ef4444', '#22c55e'];
  const BANK_COLORS = ['#dc2626', '#ea580c', '#059669', '#8b5cf6'];

  return (
    <div className="space-y-6 animate-slide-up" data-testid="dashboard-page">
      {/* Header com Filtro */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Dashboard</h1>
          <p className="text-slate-600">Visão geral do seu financeiro</p>
        </div>
        <Button
          onClick={() => setMostrarFiltros(!mostrarFiltros)}
          variant="outline"
          className="border-slate-300"
        >
          <Filter className="w-4 h-4 mr-2" />
          {mostrarFiltros ? 'Ocultar' : 'Filtrar Período'}
        </Button>
      </div>

      {/* Filtros de Período */}
      {mostrarFiltros && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={filtros.data_inicio}
                  onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
                />
              </div>
              <div>
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={filtros.data_fim}
                  onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
                />
              </div>
              <div className="flex items-end space-x-2">
                <Button onClick={loadData} className="flex-1 bg-gradient-to-r from-red-600 to-orange-600">
                  Aplicar Filtro
                </Button>
                <Button 
                  onClick={() => {
                    setFiltros({ data_inicio: '', data_fim: '' });
                    setTimeout(loadData, 100);
                  }}
                  variant="outline"
                >
                  Limpar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-hover bg-gradient-to-br from-red-50 to-white border-red-100" data-testid="card-pagar-aberto">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">A Pagar - Em Aberto</CardTitle>
            <TrendingDown className="w-5 h-5 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(data?.pagar?.em_aberto || 0)}</div>
            <p className="text-xs text-slate-600 mt-1">Valores em aberto</p>
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-orange-50 to-white border-orange-100" data-testid="card-pagar-vencido">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">A Pagar - Vencido</CardTitle>
            <AlertCircle className="w-5 h-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(data?.pagar?.vencido || 0)}</div>
            <p className="text-xs text-slate-600 mt-1">Valores vencidos</p>
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-blue-50 to-white border-blue-100" data-testid="card-receber-aberto">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">A Receber - Em Aberto</CardTitle>
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(data?.receber?.em_aberto || 0)}</div>
            <p className="text-xs text-slate-600 mt-1">Valores em aberto</p>
          </CardContent>
        </Card>

        <Card className="card-hover bg-gradient-to-br from-green-50 to-white border-green-100" data-testid="card-receber-recebido">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-semibold text-slate-700">Recebido</CardTitle>
            <DollarSign className="w-5 h-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(data?.receber?.recebido || 0)}</div>
            <p className="text-xs text-slate-600 mt-1">Total recebido</p>
          </CardContent>
        </Card>
      </div>

      {/* Cards de Alertas - Vencimentos */}
      {resumoAlertas && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-slate-900 flex items-center">
            <AlertCircle className="w-6 h-6 mr-2 text-orange-600" />
            Alertas de Vencimento
          </h2>
          
          {/* Contas a PAGAR */}
          <div>
            <h3 className="text-lg font-semibold text-red-700 mb-3">📤 Contas a Pagar</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Vencidos - Pagar */}
              <Card className="border-2 border-red-300 bg-red-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-red-800 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Vencidos ({resumoAlertas.pagar.vencidos.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-900 mb-3">
                    {formatCurrency(resumoAlertas.totais.pagar.vencidos)}
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {resumoAlertas.pagar.vencidos.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded border border-red-200">
                        <p className="font-semibold text-slate-900 truncate">{item.titulo}</p>
                        <div className="flex justify-between mt-1">
                          <span className="text-red-700 font-bold">{formatCurrency(item.valor)}</span>
                          <span className="text-red-600">Há {Math.abs(item.dias)} dias</span>
                        </div>
                      </div>
                    ))}
                    {resumoAlertas.pagar.vencidos.length === 0 && (
                      <p className="text-sm text-slate-500 italic">Nenhuma conta vencida</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Vence Hoje - Pagar */}
              <Card className="border-2 border-orange-300 bg-orange-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-orange-800 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Vence Hoje ({resumoAlertas.pagar.hoje.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-900 mb-3">
                    {formatCurrency(resumoAlertas.totais.pagar.hoje)}
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {resumoAlertas.pagar.hoje.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded border border-orange-200">
                        <p className="font-semibold text-slate-900 truncate">{item.titulo}</p>
                        <div className="flex justify-between mt-1">
                          <span className="text-orange-700 font-bold">{formatCurrency(item.valor)}</span>
                          <span className="text-orange-600">Hoje</span>
                        </div>
                      </div>
                    ))}
                    {resumoAlertas.pagar.hoje.length === 0 && (
                      <p className="text-sm text-slate-500 italic">Nenhuma conta vence hoje</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Próximos 7 Dias - Pagar */}
              <Card className="border-2 border-yellow-300 bg-yellow-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-yellow-800 flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    Próximos 7 Dias ({resumoAlertas.pagar.proximos_7_dias.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-900 mb-3">
                    {formatCurrency(resumoAlertas.totais.pagar.proximos_7_dias)}
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {resumoAlertas.pagar.proximos_7_dias.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded border border-yellow-200">
                        <p className="font-semibold text-slate-900 truncate">{item.titulo}</p>
                        <div className="flex justify-between mt-1">
                          <span className="text-yellow-700 font-bold">{formatCurrency(item.valor)}</span>
                          <span className="text-yellow-600">Em {item.dias} dias</span>
                        </div>
                      </div>
                    ))}
                    {resumoAlertas.pagar.proximos_7_dias.length === 0 && (
                      <p className="text-sm text-slate-500 italic">Nenhuma conta nos próximos 7 dias</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contas a RECEBER */}
          <div>
            <h3 className="text-lg font-semibold text-blue-700 mb-3">📥 Contas a Receber</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Vencidos - Receber */}
              <Card className="border-2 border-red-300 bg-red-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-red-800 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Vencidos ({resumoAlertas.receber.vencidos.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-900 mb-3">
                    {formatCurrency(resumoAlertas.totais.receber.vencidos)}
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {resumoAlertas.receber.vencidos.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded border border-red-200">
                        <p className="font-semibold text-slate-900 truncate">{item.titulo}</p>
                        <div className="flex justify-between mt-1">
                          <span className="text-red-700 font-bold">{formatCurrency(item.valor)}</span>
                          <span className="text-red-600">Há {Math.abs(item.dias)} dias</span>
                        </div>
                      </div>
                    ))}
                    {resumoAlertas.receber.vencidos.length === 0 && (
                      <p className="text-sm text-slate-500 italic">Nenhuma conta vencida</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Vence Hoje - Receber */}
              <Card className="border-2 border-orange-300 bg-orange-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-orange-800 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Vence Hoje ({resumoAlertas.receber.hoje.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-orange-900 mb-3">
                    {formatCurrency(resumoAlertas.totais.receber.hoje)}
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {resumoAlertas.receber.hoje.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded border border-orange-200">
                        <p className="font-semibold text-slate-900 truncate">{item.titulo}</p>
                        <div className="flex justify-between mt-1">
                          <span className="text-orange-700 font-bold">{formatCurrency(item.valor)}</span>
                          <span className="text-orange-600">Hoje</span>
                        </div>
                      </div>
                    ))}
                    {resumoAlertas.receber.hoje.length === 0 && (
                      <p className="text-sm text-slate-500 italic">Nenhuma conta vence hoje</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Próximos 7 Dias - Receber */}
              <Card className="border-2 border-yellow-300 bg-yellow-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-yellow-800 flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    Próximos 7 Dias ({resumoAlertas.receber.proximos_7_dias.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-900 mb-3">
                    {formatCurrency(resumoAlertas.totais.receber.proximos_7_dias)}
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {resumoAlertas.receber.proximos_7_dias.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded border border-yellow-200">
                        <p className="font-semibold text-slate-900 truncate">{item.titulo}</p>
                        <div className="flex justify-between mt-1">
                          <span className="text-yellow-700 font-bold">{formatCurrency(item.valor)}</span>
                          <span className="text-yellow-600">Em {item.dias} dias</span>
                        </div>
                      </div>
                    ))}
                    {resumoAlertas.receber.proximos_7_dias.length === 0 && (
                      <p className="text-sm text-slate-500 italic">Nenhuma conta nos próximos 7 dias</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Charts Row 1 - Pagar e Receber */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Contas a Pagar</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={chartDataPagar} cx="50%" cy="45%" outerRadius={85} dataKey="value">
                  {chartDataPagar.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend formatter={(value, entry) => `${value}: ${formatCurrency(entry.payload.value)}`} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">Contas a Receber</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={chartDataReceber} cx="50%" cy="45%" outerRadius={85} dataKey="value">
                  {chartDataReceber.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend formatter={(value, entry) => `${value}: ${formatCurrency(entry.payload.value)}`} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Seção Individual por Banco */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
          <Building2 className="w-6 h-6 mr-2 text-red-600" />
          Análise Detalhada por Banco
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {dadosPorBanco.map((bancoData, index) => (
            <Card key={bancoData.banco} className="card-hover">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold text-slate-900">{bancoData.banco}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                    <span className="text-xs font-semibold text-green-700">Entradas</span>
                    <span className="text-sm font-bold text-green-600">{formatCurrency(bancoData.entradas)}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                    <span className="text-xs font-semibold text-red-700">Saídas</span>
                    <span className="text-sm font-bold text-red-600">{formatCurrency(bancoData.saidas)}</span>
                  </div>
                  <div className={`flex items-center justify-between p-2 rounded-lg ${bancoData.saldo >= 0 ? 'bg-blue-50' : 'bg-orange-50'}`}>
                    <span className={`text-xs font-semibold ${bancoData.saldo >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>Saldo</span>
                    <span className={`text-sm font-bold ${bancoData.saldo >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>{formatCurrency(bancoData.saldo)}</span>
                  </div>
                </div>
                
                <div className="mt-4">
                  <ResponsiveContainer width="100%" height={110}>
                    <BarChart data={bancoData.dados} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis hide />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="value" fill={BANK_COLORS[index % BANK_COLORS.length]} radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="value" position="top" formatter={(v) => `R$${(v/1000).toFixed(0)}k`} style={{ fontSize: 9, fill: '#64748b' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Alertas */}
      {alertas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-orange-600" />
              Alertas de Vencimento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alertas.slice(0, 5).filter(a => a?.financeiro).map((alerta, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100">
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{alerta.financeiro?.titulo || '—'}</p>
                    <p className="text-sm text-slate-600">{alerta.pessoa?.nome_razao}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-slate-900">{formatCurrency(alerta.financeiro?.valor_liquido || 0)}</p>
                    <p className="text-sm text-orange-600 font-semibold">
                      {alerta.dias_vencimento === 0 ? 'Vence hoje' :
                       alerta.dias_vencimento < 0 ? `Vencido há ${Math.abs(alerta.dias_vencimento)} dias` :
                       `Vence em ${alerta.dias_vencimento} dias`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;